import { Plus, Trash2 } from 'lucide-react';
import { Button, Input } from '@/components/ui';

interface OptionListEditorProps {
  options: string[];
  disabled: boolean;
  onChange: (options: string[]) => void;
}

/**
 * 선택형(라디오/체크/셀렉트) 항목의 선택지 동적 목록. §4 "옵션 입력".
 * 문자열 1개 = 선택지 1개. shippingFee RegionSurchargeEditor 의 동적 행 패턴을
 * 문자열 단일 컬럼으로 단순화. 빈 문자열은 저장 단계에서 제거된다.
 */
export const OptionListEditor = ({
  options,
  disabled,
  onChange,
}: OptionListEditorProps) => {
  const updateRow = (index: number, value: string) => {
    onChange(options.map((o, i) => (i === index ? value : o)));
  };
  const addRow = () => onChange([...options, '']);
  const removeRow = (index: number) => onChange(options.filter((_, i) => i !== index));

  return (
    <div className="flex flex-col gap-2" data-testid="option-list-editor">
      <div
        className="overflow-hidden rounded-md"
        style={{ border: '1px solid var(--divider-soft)' }}
      >
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr style={{ background: 'var(--surface-raised)' }}>
              <th
                className="px-3 py-2 text-left font-medium"
                style={{ color: 'var(--content-muted)' }}
              >
                선택지
              </th>
              <th className="w-[44px] px-2 py-2" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {options.length === 0 ? (
              <tr>
                <td
                  colSpan={2}
                  className="px-3 py-3 text-center"
                  style={{ color: 'var(--content-muted)' }}
                >
                  선택지가 없습니다. 라디오·체크·셀렉트 유형은 1개 이상 입력하세요.
                </td>
              </tr>
            ) : null}
            {options.map((opt, oi) => (
              <tr key={oi} style={{ borderTop: '1px solid var(--divider-soft)' }}>
                <td className="px-2 py-1.5">
                  <Input
                    value={opt}
                    onChange={(e) => updateRow(oi, e.target.value)}
                    placeholder="예: 선택지 항목"
                    disabled={disabled}
                    className="h-8"
                    aria-label={`선택지 ${oi + 1}`}
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  {!disabled ? (
                    <button
                      type="button"
                      onClick={() => removeRow(oi)}
                      aria-label="선택지 삭제"
                      style={{ color: 'var(--signal-danger)', cursor: 'pointer' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!disabled ? (
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={addRow}
            data-testid="option-list-add"
          >
            <Plus size={14} aria-hidden="true" />
            선택지 추가
          </Button>
        </div>
      ) : null}
    </div>
  );
};
